/**
 * Query builder — translates structured intents into DuckDB SQL.
 * Used as a fallback when the LLM doesn't provide SQL, or to validate LLM SQL.
 *
 * Security: All user-supplied values are sanitized via escapeSQLString()
 * and attributes are validated against ALLOWED_ATTRIBUTES whitelist.
 */

import { iso3ToIso2 } from '../utils/iso-codes.js';
import { R2_PARQUET_URL } from '../config.js';

const TABLE_URLS = {
  countries: `${R2_PARQUET_URL}/data/countries.parquet`,
  states_provinces: `${R2_PARQUET_URL}/data/states-provinces.parquet`,
  cities: `${R2_PARQUET_URL}/data/cities.parquet`,
};

// ── SQL Injection Protection ──

/** Whitelist of allowed column names for SQL interpolation. */
const ALLOWED_ATTRIBUTES = new Set([
  'population', 'gdp', 'gdp_per_capita', 'area', 'density', 'pop_density',
  'continent', 'region', 'subregion', 'name', 'iso3', 'iso2',
  'life_expectancy', 'hdi', 'literacy_rate', 'unemployment',
  'co2_emissions', 'forest_area', 'urban_population', 'internet_users',
  'birth_rate', 'death_rate', 'infant_mortality', 'fertility_rate',
  'pop_growth', 'median_age', 'gini', 'poverty_rate',
  'shapeName', 'shapeISO', 'shapeGroup', 'shapeType',
  'area_km2', 'pop_density_km2', 'pop_pct_national',
  'count', 'total_pop', // aggregate aliases
]);

/** Escape single quotes in SQL string values to prevent injection. */
export function escapeSQLString(val) {
  if (val === null || val === undefined) return '';
  return String(val).replace(/'/g, "''");
}

/** Validate an attribute name against the whitelist. Returns safe name or fallback. */
export function sanitizeAttribute(attr, fallback = 'population') {
  if (!attr || typeof attr !== 'string') return fallback;
  // Allow qualified names like "t.population"
  const bare = attr.includes('.') ? attr.split('.').pop() : attr;
  if (ALLOWED_ATTRIBUTES.has(bare)) return attr;
  console.warn(`[query-builder] Rejected attribute "${attr}" — not in whitelist, using "${fallback}"`);
  return fallback;
}

// ── Continents ──

export const CONTINENTS = [
  'Africa', 'Asia', 'Europe', 'North America', 'South America', 'Oceania', 'Antarctica',
];

// ── Public API ──

/**
 * Get the R2 URL for a dataset.
 */
export function getTableUrl(dataset) {
  return TABLE_URLS[dataset] || TABLE_URLS.countries;
}

/**
 * Get the current R2 base URL.
 */
export function getR2Url() {
  return R2_PARQUET_URL;
}

/**
 * Build a SQL query from a structured intent.
 * Returns null if the intent doesn't need SQL (e.g., simple navigation).
 */
export function buildSQL(intent) {
  const { type, target, operation } = intent;

  // Navigation doesn't need SQL
  if (type === 'navigation') return null;

  const dataset = target?.dataset || 'countries';
  const tableUrl = getTableUrl(dataset);
  const table = `'${escapeSQLString(tableUrl)}'`;

  switch (type) {
    case 'choropleth':
      return buildChoroplethSQL(table, dataset, target, operation);
    case 'filter':
      return buildFilterSQL(table, dataset, target, operation);
    case 'compare':
      return buildCompareSQL(table, dataset, target, operation);
    case 'analyze':
      return buildAnalyzeSQL(table, dataset, target, operation);
    case 'show':
      return buildShowSQL(table, dataset, target, operation);
    default:
      return null;
  }
}

function buildChoroplethSQL(table, dataset, target, operation) {
  const attr = sanitizeAttribute(operation?.attribute, 'population');
  const where = buildLocationFilter(dataset, target);
  return `SELECT *, ST_AsGeoJSON(geometry) as geojson FROM ${table}${where} ORDER BY ${attr} DESC`;
}

function buildFilterSQL(table, dataset, target, operation) {
  const attr = sanitizeAttribute(operation?.attribute, 'population');
  let where = buildLocationFilter(dataset, target);

  if (operation?.action === 'rank' && operation?.filter) {
    const count = Math.max(1, Math.min(parseInt(operation.filter.value, 10) || 10, 1000));
    return `SELECT *, ST_AsGeoJSON(geometry) as geojson FROM ${table}${where} ORDER BY ${attr} DESC LIMIT ${count}`;
  }

  if (operation?.action === 'filterByAttribute' && operation?.filter) {
    const filterClause = buildFilterClause(attr, operation.filter);
    where = where ? `${where} AND ${filterClause}` : ` WHERE ${filterClause}`;
    return `SELECT *, ST_AsGeoJSON(geometry) as geojson FROM ${table}${where} ORDER BY ${attr} DESC`;
  }

  return `SELECT *, ST_AsGeoJSON(geometry) as geojson FROM ${table}${where} ORDER BY ${attr} DESC`;
}

function buildCompareSQL(table, dataset, target, operation) {
  if (operation?.filter?.value && Array.isArray(operation.filter.value)) {
    const codes = operation.filter.value.map(c => `'${escapeSQLString(c)}'`).join(', ');
    return `SELECT *, ST_AsGeoJSON(geometry) as geojson FROM ${table} WHERE iso3 IN (${codes})`;
  }
  return buildChoroplethSQL(table, dataset, target, operation);
}

function buildAnalyzeSQL(table, dataset, target, operation) {
  const attr = sanitizeAttribute(operation?.attribute, 'continent');
  if (operation?.action === 'count') {
    const where = buildLocationFilter(dataset, target);
    return `SELECT ${attr}, COUNT(*) as count, SUM(population) as total_pop FROM ${table}${where} GROUP BY ${attr} ORDER BY count DESC`;
  }
  return buildChoroplethSQL(table, dataset, target, operation);
}

function buildShowSQL(table, dataset, target, operation) {
  const where = buildLocationFilter(dataset, target);
  return `SELECT *, ST_AsGeoJSON(geometry) as geojson FROM ${table}${where}`;
}

function buildLocationFilter(dataset, target) {
  const parts = [];

  if (target?.country && dataset === 'countries') {
    parts.push(`iso3 = '${escapeSQLString(target.country)}'`);
  } else if (target?.country && dataset === 'states_provinces') {
    const iso2 = iso3ToIso2(target.country) || target.country.substring(0, 2);
    parts.push(`iso2 = '${escapeSQLString(iso2)}'`);
  } else if (target?.country && dataset === 'cities') {
    const iso2 = iso3ToIso2(target.country) || target.country.substring(0, 2);
    parts.push(`iso2 = '${escapeSQLString(iso2)}'`);
  }

  if (target?.location && !target?.country) {
    const matchedContinent = CONTINENTS.find(
      c => c.toLowerCase() === target.location.toLowerCase()
    );
    if (matchedContinent && dataset === 'countries') {
      parts.push(`continent = '${escapeSQLString(matchedContinent)}'`);
    }
  }

  return parts.length > 0 ? ` WHERE ${parts.join(' AND ')}` : '';
}

function buildFilterClause(attr, filter) {
  const { operator, value } = filter;

  // Numeric operators — coerce to number to prevent injection
  if (['>', '<', '>=', '<='].includes(operator)) {
    const num = Number(value);
    if (!Number.isFinite(num)) return '1=1';
    return `${attr} ${operator} ${num}`;
  }

  switch (operator) {
    case '==':
      return typeof value === 'string'
        ? `${attr} ILIKE '${escapeSQLString(value)}'`
        : `${attr} = ${Number.isFinite(Number(value)) ? Number(value) : 0}`;
    case '!=':
      return typeof value === 'string'
        ? `${attr} NOT ILIKE '${escapeSQLString(value)}'`
        : `${attr} != ${Number.isFinite(Number(value)) ? Number(value) : 0}`;
    case 'contains':
      return `${attr} ILIKE '%${escapeSQLString(value)}%'`;
    default:
      return '1=1';
  }
}
